import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useGetJobCardQuery } from '@/redux/internal-services/jobsApi';
import { useDownloadJobCardPdf } from '@/components/Jobs/useDownloadJobCardPdf';
import { Spinner } from '@/components/UI/Spinner';
import MaterialListModal from '@/components/Jobs/MaterialListModal';
import { useSamSectionContext } from '@/hooks';

const JobCardDisplayCard: React.FC<{
  engagementId: string;
  notesCount?: number;
  onOpenProposal?: () => void;
  onOpenAssessment?: () => void;
  onOpenNotes?: () => void;
}> = ({ engagementId, notesCount, onOpenProposal, onOpenAssessment, onOpenNotes }) => {
  const { t } = useTranslation();
  const [materialModalOpen, setMaterialModalOpen] = useState(false);
  useSamSectionContext('job_card', { engagement_id: engagementId });
  const { data: card, isLoading, isFetching } = useGetJobCardQuery(engagementId, { skip: !engagementId });
  const { download: downloadJobCard, downloading: downloadingPdf, error: pdfError } = useDownloadJobCardPdf(engagementId);

  if (isLoading) {
    return <p className="py-2 text-xs text-ui-text3">{t('common.loading', 'Loading…')}</p>;
  }
  if (!card) return null;

  return (
    <>
      <div className="border-b border-ui-border px-3 py-3">
        {/* Header row */}
        <div className="flex items-center justify-between mb-3">
          <span className="text-[10px] font-semibold uppercase tracking-widest text-ui-text3">
            {t('jobs.jobCard', 'Job Card')}
          </span>
          <button
            type="button"
            onClick={() => void downloadJobCard()}
            disabled={downloadingPdf}
            className="flex items-center gap-1 rounded-lg border border-brand-dark/40 px-2.5 py-1 text-[11px] font-medium text-brand-dark hover:bg-brand-light/10 disabled:opacity-60 transition-colors"
          >
            {isFetching && !downloadingPdf && <Spinner size="sm" />}
            {downloadingPdf ? t('jobs.downloading', 'Downloading…') : `↓ ${t('jobs.downloadFullJobCard', 'Download full job card')}`}
          </button>
        </div>

        {pdfError && <p className="mb-2 text-[10px] text-[#CF4148]">{pdfError}</p>}

        {/* Quick-link tab buttons */}
        <div className="flex flex-wrap gap-1.5 mb-3">
          <button
            type="button"
            onClick={onOpenProposal}
            className="flex items-center gap-1 rounded-lg border border-ui-border px-2.5 py-1.5 text-[11px] font-medium text-ui-text2 hover:border-brand-dark hover:bg-brand-light/10 transition-colors"
          >
            <span className="text-[10px]">▤</span>
            {t('jobs.proposalDetails', 'Proposal details')}
          </button>
          <button
            type="button"
            onClick={onOpenAssessment}
            className="flex items-center gap-1 rounded-lg border border-ui-border px-2.5 py-1.5 text-[11px] font-medium text-ui-text2 hover:border-brand-dark hover:bg-brand-light/10 transition-colors"
          >
            <span className="text-[10px]">▤</span>
            {t('jobs.assessmentDetails', 'Assessment details')}
          </button>
          <button
            type="button"
            onClick={() => setMaterialModalOpen(true)}
            className="flex items-center gap-1 rounded-lg border border-ui-border px-2.5 py-1.5 text-[11px] font-medium text-ui-text2 hover:border-brand-dark hover:bg-brand-light/10 transition-colors"
          >
            <span className="text-[10px]">⧉</span>
            {t('jobs.materialList', 'Material list')}
          </button>
          <button
            type="button"
            onClick={onOpenNotes}
            className="flex items-center gap-1 rounded-lg border border-ui-border px-2.5 py-1.5 text-[11px] font-medium text-ui-text2 hover:border-brand-dark hover:bg-brand-light/10 transition-colors"
          >
            <span className="text-[10px]">✎</span>
            {notesCount != null && notesCount > 0
              ? `${t('jobs.notes', 'Notes')} (${notesCount})`
              : t('jobs.notes', 'Notes')}
          </button>
        </div>

        {/* Job card fields — rows separated by dividers */}
        <div className="divide-y divide-ui-border text-xs text-ui-text">
          {/* Row 1: Project Manager | Assigned by */}
          <div className="grid grid-cols-2 gap-x-4 py-2.5">
            <div>
              <p className="mb-0.5 text-[10px] font-medium text-brand-dark">
                {t('jobs.projectManager', 'Project Manager')}
              </p>
              {card.project_manager_name ? (
                <p className="text-ui-text">
                  {card.project_manager_name}
                  {card.project_manager_phone && (
                    <span className="text-ui-text3"> · {card.project_manager_phone}</span>
                  )}
                </p>
              ) : (
                <p className="text-ui-text3">{t('jobs.notAssignedYet', 'Not assigned yet')}</p>
              )}
            </div>
            <div>
              <p className="mb-0.5 text-[10px] font-medium text-brand-dark">
                {t('jobs.assignedBy', 'Assigned by')}
              </p>
              <p className="text-ui-text">
                {card.pm_assigned_by_name
                  ? `${card.pm_assigned_by_name}${card.pm_assigned_on ? ` · ${new Date(card.pm_assigned_on).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}` : ''}`
                  : '—'}
              </p>
            </div>
          </div>

          {/* Row 2: Accepted proposal | Contract value */}
          <div className="grid grid-cols-2 gap-x-4 py-2.5">
            <div>
              <p className="mb-0.5 text-[10px] font-medium text-brand-dark">
                {t('jobs.acceptedProposal', 'Accepted proposal')}
              </p>
              {card.accepted_proposal_number ? (
                <p className="text-ui-text">
                  {card.accepted_proposal_number}
                  {card.accepted_proposal_date && (
                    <span className="text-ui-text3">
                      {' '}· {t('jobs.acceptedOn', 'accepted {{date}}', {
                        date: new Date(card.accepted_proposal_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
                      })}
                    </span>
                  )}
                </p>
              ) : (
                <p className="text-ui-text3">{t('jobs.notYetAccepted', 'Not yet accepted')}</p>
              )}
            </div>
            <div>
              <p className="mb-0.5 text-[10px] font-medium text-brand-dark">
                {t('jobs.contractValue', 'Contract value')}
              </p>
              {card.contract_value_restricted ? (
                <p className="text-ui-text3">{t('jobs.restricted', 'Restricted')}</p>
              ) : card.contract_value != null ? (
                <p className="text-ui-text">
                  {card.contract_value.toLocaleString(undefined, { style: 'currency', currency: 'USD' })}
                </p>
              ) : (
                <p className="text-ui-text3">—</p>
              )}
            </div>
          </div>

          {/* Row 3: Scheduled window | Assigned crew */}
          <div className="grid grid-cols-2 gap-x-4 py-2.5">
            <div>
              <p className="mb-0.5 text-[10px] font-medium text-brand-dark">
                {t('jobs.schedule', 'Scheduled window')}
              </p>
              <p className="text-ui-text">
                {card.schedule?.scheduled_date ? (() => {
                  const datePart = new Date(card.schedule!.scheduled_date! + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
                  const st = card.schedule!.start_time;
                  if (!st) return datePart;
                  const m24 = st.match(/^(\d{1,2}):(\d{2})$/);
                  const m12 = st.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
                  let h = 0, min = 0;
                  if (m24) { h = +m24[1]; min = +m24[2]; }
                  else if (m12) {
                    h = +m12[1]; min = +m12[2];
                    if (m12[3].toUpperCase() === 'PM' && h !== 12) h += 12;
                    if (m12[3].toUpperCase() === 'AM' && h === 12) h = 0;
                  }
                  const startD = new Date(2000, 0, 1, h, min);
                  const startStr = startD.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
                  if (!card.schedule!.duration_minutes) return `${datePart} · ${startStr}`;
                  const endD = new Date(startD.getTime() + card.schedule!.duration_minutes * 60000);
                  const endStr = endD.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
                  return `${datePart} · ${startStr}–${endStr}`;
                })() : <span className="text-ui-text3">{t('jobs.notScheduledYet', 'Not scheduled yet')}</span>}
              </p>
            </div>
            <div>
              <p className="mb-0.5 text-[10px] font-medium text-brand-dark">
                {t('jobs.crew', 'Assigned crew')}
              </p>
              <p className="text-ui-text">
                {card.crew?.name ?? <span className="text-ui-text3">{t('jobs.notAssignedYet', 'Not assigned yet')}</span>}
              </p>
            </div>
          </div>

          {/* Row 4: Crew lead | Materials — only when either is available */}
          {(card.crew_lead_name || card.materials_delivery_note) && (
            <div className="grid grid-cols-2 gap-x-4 py-2.5">
              <div>
                <p className="mb-0.5 text-[10px] font-medium text-brand-dark">
                  {t('jobs.crewLead', 'Crew lead')}
                </p>
                {card.crew_lead_name ? (
                  <p className="text-ui-text">
                    {card.crew_lead_name}
                    {card.crew_lead_phone && <span className="text-ui-text3"> · {card.crew_lead_phone}</span>}
                  </p>
                ) : <p className="text-ui-text3">—</p>}
              </div>
              <div>
                <p className="mb-0.5 text-[10px] font-medium text-brand-dark">
                  {t('jobs.materials', 'Materials')}
                </p>
                <p className="text-ui-text">{card.materials_delivery_note ?? <span className="text-ui-text3">—</span>}</p>
              </div>
            </div>
          )}

          {/* Row 5: Execution | Field work — only when started */}
          {(card.execution_started_at || card.field_work_completed_at) && (
            <div className="grid grid-cols-2 gap-x-4 py-2.5">
              <div>
                <p className="mb-0.5 text-[10px] font-medium text-brand-dark">
                  {t('jobs.execution', 'Execution')}
                </p>
                {card.execution_started_at ? (
                  <p className="text-ui-text">
                    {t('jobs.startedOn', 'Started {{date}}', {
                      date: new Date(card.execution_started_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) +
                        ', ' + new Date(card.execution_started_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }),
                    })}
                  </p>
                ) : <p className="text-ui-text3">—</p>}
              </div>
              <div>
                <p className="mb-0.5 text-[10px] font-medium text-brand-dark">
                  {t('jobs.fieldWork', 'Field work')}
                </p>
                {card.field_work_completed_at ? (
                  <p className="text-ui-text">
                    {t('jobs.completedOn', 'Completed {{date}}', {
                      date: new Date(card.field_work_completed_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) +
                        ', ' + new Date(card.field_work_completed_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }),
                    })}
                  </p>
                ) : <p className="text-ui-text3">—</p>}
              </div>
            </div>
          )}

          {/* Row 6: QA result | Invoice — only from qa_inspected onward */}
          {card.qa_result_summary && (
            <div className="grid grid-cols-2 gap-x-4 py-2.5">
              <div>
                <p className="mb-0.5 text-[10px] font-medium text-brand-dark">
                  {t('jobs.qaResult', 'QA result')}
                </p>
                <p className="text-ui-text">
                  {card.qa_result_summary === 'pass'
                    ? t('jobs.qaPassedOn', 'Passed{{date}}', {
                        date: card.qa_passed_at
                          ? ' ' + new Date(card.qa_passed_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) +
                            ', ' + new Date(card.qa_passed_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
                          : '',
                      })
                    : t('jobs.qaFailed', 'Failed')}
                </p>
              </div>
              <div>
                <p className="mb-0.5 text-[10px] font-medium text-brand-dark">
                  {t('jobs.invoice', 'Invoice')}
                </p>
                <p className="text-ui-text3">{t('jobs.restricted', 'Restricted')}</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {materialModalOpen && (
        <MaterialListModal engagementId={engagementId} onClose={() => setMaterialModalOpen(false)} />
      )}
    </>
  );
};

export default JobCardDisplayCard;
